import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

import Sidebar from "./components/Sidebar/Sidebar";
import Dashboard from "./pages/Dashboard";
import SubscriptionScreen from "./components/Subscription/SubscriptionScreen";
import PrivacyPolicy from "./components/Legal/PrivacyPolicy";
import TermsConditions from "./components/Legal/TermsConditions";
import RefundPolicy from "./components/Legal/RefundPolicy";
import FeedbackScreen from "./components/Feedback/FeedbackScreen";
import ScheduledReportsScreen from "./components/Reports/ScheduledReportsScreen";

import Homepage from "./components/Home/Homepage";
import DemoExperience from "./components/Demo/DemoExperience";
import Logo from "./components/common/Logo";
import { useAuth } from "./context/AuthContext";

import { getUserSessions } from "./firebase/firestore";


// ============================================================
// AUTHENTICATED APP
// ============================================================

function AuthenticatedApp() {

  const { user } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);


  // ----------------------------------
  // LOAD USER SESSIONS
  // ----------------------------------

  useEffect(() => {

    if (!user) return;

    loadSessions();

  }, [user]);


  async function loadSessions() {

    try {

      const userSessions = await getUserSessions(user.uid);

      setSessions(userSessions);

      // Scheduled report emails link to /?session=<id> (see functions/
      // index.js dashboardUrlFor). getUserSessions() only ever returns
      // sessions owned by the signed-in user, so matching against that
      // list is what keeps this from being an open/predictable link to
      // someone else's data — a recipient who isn't the owner simply
      // won't find a match and falls back to their own most recent
      // session, same as opening the app normally.
      const requestedId = new URLSearchParams(location.search).get("session");
      const requestedSession = requestedId
        ? userSessions.find((s) => s.firebaseId === requestedId)
        : null;

      if (requestedSession) {
        setActiveSession(requestedSession);
      } else if (userSessions.length > 0) {
        setActiveSession(userSessions[0]);
      }

    } catch (err) {

      console.error(err);

    }

  }


  // ----------------------------------
  // CURRENT VIEW
  // ----------------------------------

  const getCurrentView = () => {

    switch (location.pathname) {

      case "/subscription":
        return "subscription";

      case "/reports":
        return "reports";

      case "/privacy":
        return "privacy";

      case "/terms":
        return "terms";

      case "/refund-policy":
        return "refund-policy";

      case "/feedback":
        return "feedback";

      default:
        return "dashboard";

    }

  };


  const view = getCurrentView();


  // ----------------------------------
  // NAVIGATION
  // ----------------------------------

  const setView = (newView) => {

    switch (newView) {

      case "subscription":
        navigate("/subscription");
        break;

      case "reports":
        navigate("/reports");
        break;

      case "privacy":
        navigate("/privacy");
        break;

      case "terms":
        navigate("/terms");
        break;

      case "refund-policy":
        navigate("/refund");
        break;

      case "feedback":
        navigate("/feedback");
        break;

      case "dashboard":
      default:
        navigate("/");
        break;

    }

  };


  return (

    <div className="h-screen flex bg-gray-950 text-white overflow-hidden">


      {/* Mobile hamburger */}

      <button
        onClick={() => setMobileNavOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-50 w-10 h-10 rounded-xl bg-gray-900 border border-gray-800 flex items-center justify-center text-white shadow-lg"
        aria-label="Open menu"
      >

        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >

          <path d="M4 6h16M4 12h16M4 18h16" />

        </svg>

      </button>


      <Sidebar
        user={user}
        sessions={sessions}
        setSessions={setSessions}
        setActiveSession={setActiveSession}
        activeSession={activeSession}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        view={view}
        setView={setView}
      />


      <Routes>

        {/* DASHBOARD */}

        <Route
          path="/"
          element={
            <Dashboard
              user={user}
              activeSession={activeSession}
              setSessions={setSessions}
              setActiveSession={setActiveSession}
              onOpenSubscription={() => navigate("/subscription")}
            />
          }
        />


        {/* SUBSCRIPTION */}

        <Route
          path="/subscription"
          element={
            <SubscriptionScreen
              onClose={() => navigate("/")}
            />
          }
        />


        {/* SCHEDULED REPORTS */}

        <Route
          path="/reports"
          element={
            <ScheduledReportsScreen
              sessions={sessions}
              onClose={() => navigate("/")}
              onOpenSubscription={() => navigate("/subscription")}
            />
          }
        />


        {/* PRIVACY */}

        <Route
          path="/privacy"
          element={
            <PrivacyPolicy
              onClose={() => navigate("/")}
            />
          }
        />


        {/* TERMS */}

        <Route
          path="/terms"
          element={
            <TermsConditions
              onClose={() => navigate("/")}
            />
          }
        />


        {/* REFUND POLICY */}

        <Route
          path="/refund"
          element={
            <RefundPolicy
              onClose={() => navigate("/")}
            />
          }
        />


        {/* FEEDBACK */}

        <Route
          path="/feedback"
          element={
            <FeedbackScreen
              onClose={() => navigate("/")}
            />
          }
        />


        {/* UNKNOWN ROUTES */}

        <Route
          path="*"
          element={<Navigate to="/" replace />}
        />

      </Routes>

    </div>

  );

}


// ============================================================
// PUBLIC LEGAL PAGES
// ============================================================
//
// Privacy, Terms, and Refund Policy must work as direct URLs
// (e.g. https://insightpulseai.app/refund-policy) and must not
// require the user to be signed in. They're checked here, before
// the auth-gated Homepage/AuthenticatedApp split below, so they
// render immediately — even while the Firebase auth check is
// still resolving — instead of requiring a login first.
//
// Signed-in users still get the fuller, sidebar-integrated version
// of these same pages via the routes inside AuthenticatedApp.

const PUBLIC_LEGAL_PAGES = {
  "/privacy": PrivacyPolicy,
  "/terms": TermsConditions,
  "/refund-policy": RefundPolicy,
};

// ============================================================
// MAIN APP
// ============================================================

function App() {

  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const PublicLegalPage = !user && PUBLIC_LEGAL_PAGES[location.pathname];

  if (PublicLegalPage) {

    return <PublicLegalPage onClose={() => navigate("/")} />;

  }


  // The demo works without authentication and, like the legal pages above,
  // is checked before the auth-loading gate so it never has to wait on a
  // Firebase auth check that's irrelevant to it. A signed-in user visiting
  // /demo still lands here for a moment until `loading` settles below,
  // then falls through to their real AuthenticatedApp on the next render —
  // same tradeoff the legal pages already make.
  if (!user && location.pathname === "/demo") {

    return <DemoExperience onExit={() => navigate("/")} />;

  }


  if (loading) {

    return (

      <div className="h-screen flex flex-col gap-4 justify-center items-center bg-gray-950 text-white">

        <Logo
          size={48}
          className="animate-pulse"
        />

        Loading InsightPulse AI...

      </div>

    );

  }


  if (!user) {

    return <Homepage />;

  }


  return <AuthenticatedApp />;

}


// ============================================================
// ROUTER
// ============================================================

export default function AppWithRouter() {

  return (

    <BrowserRouter>

      <App />

    </BrowserRouter>

  );

}